import type {
  AgendaItem,
  AgendaModule,
  Document,
  Meeting,
  User,
} from "@repo/types";

export type EditorType = "meeting" | "agendaModule" | "agendaItem" | "document";
export type EditorObject = Meeting | AgendaModule | AgendaItem | Document;

export interface Collapsible {
  id: string;
  type: "agendaModule" | "agendaItem";
  canCollapse: () => boolean;
  setCollapsed: (collapsed: boolean) => void;
  isCollapsed: () => boolean;
}

/** Utility for obtaining a user's name as a string. */
export const userName = (
  user: string | undefined,
  userMap: Record<string, User>
): React.ReactNode => {
  const userObj = userMap[user ?? ""];
  return userObj
    ? `${userObj.firstName} ${userObj.lastName}`
    : user === "none"
      ? "Nobody"
      : user === "null"
        ? "N/A"
        : "<Unknown>";
};

/** A hook for helping with tracking multiple occurrences of the same id.
 *
 * Call `trackInd` to increment the number of times an id was seen.
 * Call `getInd` to see how many times the id was tracked. */
export const useIndTracker = () => {
  const seen = new Map<string, number>();

  const getInd = (id: string) => seen.get(id) ?? 0;
  const trackInd = (id: string) => {
    const ind = getInd(id);
    seen.set(id, ind + 1);
    return ind;
  };
  return { getInd, trackInd };
};
